import { useEffect, useState } from 'react';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { TextInput } from '../../../components/TextInput';
import type { CreateBillingInvoiceRequest } from '../../../types/adminBilling';
import { dollarsToCents } from './billingFormat';

interface NewInvoiceModalProps {
  isOpen: boolean;
  organizationName: string;
  defaultCurrency: string;
  saving: boolean;
  error: string | null;
  onCreate: (request: CreateBillingInvoiceRequest) => void;
  onClose: () => void;
}

const isDateOnly = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

/**
 * A hand-made invoice, for the charge that is not on the plan.
 *
 * Only the amount is required. Every date is optional and **omitted from the body when blank**,
 * rather than sent as an empty string or as today's date: the API derives an issue date and a due
 * date from the plan's payment terms, and guessing them here is how an invoice comes to be due on
 * a different schedule from every other one on the same organization.
 *
 * Every date field is a `"yyyy-MM-dd"` string from the date input straight through to the request.
 * Nothing goes via a `Date`, so nothing shifts by the admin's UTC offset.
 */
export const NewInvoiceModal = ({
  isOpen,
  organizationName,
  defaultCurrency,
  saving,
  error,
  onCreate,
  onClose,
}: NewInvoiceModalProps) => {
  const [amount, setAmount] = useState('');
  const [issuedOn, setIssuedOn] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setAmount('');
    setIssuedOn('');
    setDueOn('');
    setPeriodStart('');
    setPeriodEnd('');
    setAmountError(null);
  }, [isOpen]);

  const handleConfirm = () => {
    const amountCents = dollarsToCents(amount);
    if (amountCents === null || amountCents <= 0) {
      setAmountError('Enter an amount in dollars, for example 1234.56.');
      return;
    }
    setAmountError(null);

    onCreate({
      amountCents,
      currency: defaultCurrency,
      ...(isDateOnly(issuedOn) ? { issuedOn } : {}),
      ...(isDateOnly(dueOn) ? { dueOn } : {}),
      ...(isDateOnly(periodStart) ? { periodStart } : {}),
      ...(isDateOnly(periodEnd) ? { periodEnd } : {}),
    });
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="New invoice"
      message={`Raise an invoice for "${organizationName}" outside the plan's schedule.`}
      confirmText={saving ? 'Creating...' : 'Create invoice'}
      busy={saving}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <div className="billing-form">
        <TextInput
          label="Amount (dollars)"
          inputMode="decimal"
          placeholder="1234.56"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={amountError ?? undefined}
          disabled={saving}
          data-testid="billing-invoice-amount"
        />
        <TextInput
          label="Issued on (optional)"
          type="date"
          value={issuedOn}
          onChange={(e) => setIssuedOn(e.target.value)}
          disabled={saving}
          data-testid="billing-invoice-issued"
        />
        <TextInput
          label="Due on (optional)"
          type="date"
          value={dueOn}
          onChange={(e) => setDueOn(e.target.value)}
          disabled={saving}
          data-testid="billing-invoice-due"
        />
        <TextInput
          label="Period start (optional)"
          type="date"
          value={periodStart}
          onChange={(e) => setPeriodStart(e.target.value)}
          disabled={saving}
          data-testid="billing-invoice-period-start"
        />
        <TextInput
          label="Period end (optional)"
          type="date"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
          disabled={saving}
          data-testid="billing-invoice-period-end"
        />
        <p className="billing-form__hint">
          Leave a date blank and the server fills it in from the plan. The dates are calendar days
          with no time zone, so every admin sees the day that was entered.
        </p>
        {error && (
          <p className="billing-form__error" role="alert" data-testid="billing-invoice-error">
            {error}
          </p>
        )}
      </div>
    </ConfirmModal>
  );
};
